/**
 * PendingSyncWarning — تحذير عند وجود عمليات لم تُزامن بعد
 *
 * يعرض:
 * 1. Banner ثابت أعلى الصفحة عندما تكون هناك عمليات معلقة
 * 2. beforeunload alert يمنع المستخدم من إغلاق التبويب
 */
import { useState, useEffect } from "react";
import { AlertTriangle, RefreshCw, X } from "lucide-react";
import { Button } from "./ui/button";
import { onOfflineChange, syncQueue, getPendingCount } from "../offline/interceptor";
import toast from "react-hot-toast";

export default function PendingSyncWarning() {
  const [pendingCount, setPendingCount] = useState(0);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [dismissed, setDismissed] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    getPendingCount().then(setPendingCount).catch(() => {});

    const unsub = onOfflineChange(({ pendingCount, online: o }) => {
      setPendingCount(pendingCount);
      setOnline(o);
      if (pendingCount > 0) setDismissed(false); // re-show on new pending
    });

    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    // beforeunload — تحذير المتصفح قبل الإغلاق إذا في عمليات معلقة
    const onBeforeUnload = (e) => {
      // قراءة من الـ closure الحالي
      // (يأخذ القيمة من state عبر الـ effect مرة أخرى — لذا نستخدم getPendingCount مباشر)
      // ملاحظة: standard pattern — string مخصص يتجاهله Chrome مع نص افتراضي
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      unsub();
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, []);

  // beforeunload effect — يستمع لقيمة pendingCount الحديثة
  useEffect(() => {
    const handler = (e) => {
      if (pendingCount > 0) {
        const msg = `لديك ${pendingCount} عملية لم تُزامن بعد! إذا أغلقت التطبيق الآن قد تفقدها.`;
        e.preventDefault();
        e.returnValue = msg; // معظم المتصفحات تعرض رسالتها العامة
        return msg;
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [pendingCount]);

  const handleSyncNow = async () => {
    if (syncing || !online) {
      if (!online) toast.error("لا يوجد اتصال بالإنترنت");
      return;
    }
    setSyncing(true);
    try {
      const res = await syncQueue();
      if (res.synced > 0) toast.success(`تمت مزامنة ${res.synced} عملية ✓`);
      if (res.failed > 0) toast.error(`فشلت ${res.failed} عملية — ستُعاد المحاولة`);
      if (res.synced === 0 && res.failed === 0) toast("لا توجد عمليات", { icon: "✓" });
      const c = await getPendingCount();
      setPendingCount(c);
    } finally {
      setSyncing(false);
    }
  };

  // لا تُظهر شي إذا ما في عمليات معلقة
  if (pendingCount === 0 || dismissed) return null;

  const bgColor = online ? "from-amber-600 to-orange-600" : "from-rose-600 to-rose-700";
  const message = online
    ? `لديك ${pendingCount} عملية في انتظار المزامنة — يجب الانتظار حتى ترسل للسيرفر`
    : `لديك ${pendingCount} عملية محفوظة محلياً — لن تُرسل حتى يعود الإنترنت ⚠️`;

  return (
    <div
      className={`bg-gradient-to-l ${bgColor} text-white px-4 py-2.5 shadow-lg sticky top-0 z-50 print:hidden`}
      data-testid="pending-sync-banner"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <AlertTriangle className="w-5 h-5 shrink-0 animate-pulse" />
          <span className="text-sm font-semibold truncate">{message}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {online && (
            <Button
              size="sm"
              onClick={handleSyncNow}
              disabled={syncing}
              className="bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-sm h-8"
              data-testid="banner-sync-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 me-1.5 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "يزامن..." : "مزامنة الآن"}
            </Button>
          )}
          <button
            onClick={() => setDismissed(true)}
            className="p-1 rounded hover:bg-white/20 transition"
            aria-label="إخفاء"
            data-testid="banner-dismiss-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
